import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Activity, AlertTriangle, Bot, CheckCircle2, Clock3, Database, Gauge, Radio,
  RefreshCw, Scale, Search, ShieldAlert, Signal, Target, Waves, Zap,
} from "lucide-react";

import PerpFlowPanel from "./PerpFlowPanel";
import WeexMarketPanel from "./WeexMarketPanel";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const SYMBOLS = ["BTC", "ETH", "SOL", "XRP"];
const TIMEFRAMES = ["5m", "15m", "30m", "1h"];
const SOURCE_NAMES = ["Gate.io", "MEXC", "OKX", "Bitget"];

const num = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const priceText = (value) => {
  const price = num(value);
  if (price >= 1000) return price.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return price >= 10 ? price.toFixed(2) : price.toFixed(4);
};

function normalizeLive(raw, symbol, timeframe) {
  const data = raw?.snapshot || raw;
  if (!data || !num(data.price || data.mark_price)) return null;
  const rawSources = data.sources || {};
  return {
    snapshotId: data.snapshot_id || data.id || `${symbol}-${timeframe}`,
    symbol: data.symbol || `${symbol}USDT`, timeframe: data.timeframe || timeframe,
    price: num(data.price || data.mark_price), quality: num(data.data_quality ?? data.quality) * (num(data.data_quality ?? data.quality) <= 1 ? 100 : 1),
    regime: data.regime || "UNCLASSIFIED",
    oiImpulse: num(data.oi_impulse ?? data.metrics?.oi_impulse),
    flowDelta: num(data.flow_delta ?? data.metrics?.flow_delta),
    funding: num(data.funding ?? data.metrics?.funding),
    cascadeRisk: num(data.cascade_risk ?? data.metrics?.cascade_risk),
    sources: SOURCE_NAMES.map((name) => {
      const source = Array.isArray(rawSources)
        ? rawSources.find((item) => String(item?.name).toLowerCase() === name.toLowerCase())
        : rawSources[name] || rawSources[name.toLowerCase()];
      return { name, state: source?.state || source?.status || (source === true ? "live" : "missing") };
    }),
    zones: (data.liquidation_zones || data.zones || []).map((zone) => ({
      side: String(zone.side || zone.position_side || "").toLowerCase(),
      leverage: num(zone.leverage), price: num(zone.price || zone.level),
      confidence: num(zone.confidence || zone.score),
    })).filter((zone) => zone.price && ["long", "short"].includes(zone.side)),
  };
}

async function json(url, options = {}) {
  const controller = new AbortController();
  // 20s, not 5s: this backend's event loop is shared with the trading engines
  // and routinely queues requests for several seconds under Hub polling.
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    if (!response.ok) throw new Error(String(response.status));
    return response.json();
  } finally {
    clearTimeout(timeout);
  }
}

function Metric({ icon: Icon, label, value, detail, tone = "neutral" }) {
  return (
    <div className={`market-room-metric is-${tone}`}>
      <div className="market-room-metric-head"><Icon className="h-4 w-4" /><span>{label}</span></div>
      <strong>{value}</strong><small>{detail}</small>
    </div>
  );
}

function SourceStrip({ sources, wsConnected }) {
  return (
    <div className="market-room-sources">
      <div className="market-room-source-heading">
        <Database className="h-4 w-4" /><span>Shared snapshot</span>
        <b>{wsConnected ? "LINKED" : "RELINK"}</b>
      </div>
      <div className="market-room-source-list">
        {sources.map((source) => {
          const state = String(source.state || "missing").toLowerCase();
          const tone = ["live", "ready", "healthy"].includes(state) ? "live" : "missing";
          return <div className="market-room-source" key={source.name}><i className={`is-${tone}`} /><span>{source.name}</span><small>{state.toUpperCase()}</small></div>;
        })}
      </div>
    </div>
  );
}

function Ladder({ snapshot }) {
  if (!snapshot?.zones?.length) {
    return <section className="market-room-panel market-room-empty"><Radio className="h-7 w-7" /><strong>No liquidation snapshot</strong><span>WAITING FOR /api/market-room/snapshot</span></section>;
  }
  const values = [snapshot.price, ...snapshot.zones.map((zone) => zone.price)];
  const low = Math.min(...values) * 0.997;
  const high = Math.max(...values) * 1.003;
  const range = Math.max(high - low, snapshot.price * 0.01);
  const y = (price) => ((high - price) / range) * 100;
  return (
    <section className="market-room-panel market-room-ladder-panel">
      <div className="market-room-panel-head">
        <div><span>Liquidation field</span><strong>{snapshot.symbol} / {snapshot.timeframe}</strong></div>
        <div className="market-room-map-key"><i className="is-short" />Short<i className="is-long" />Long</div>
      </div>
      <div className="market-room-ladder">
        <div className="market-room-price-axis"><span>{priceText(high)}</span><span>{priceText(low)}</span></div>
        {snapshot.zones.map((zone, index) => (
          <div key={`${zone.side}-${zone.leverage}-${index}`} className={`market-room-zone is-${zone.side}`}
            style={{ "--zone-y": `${y(zone.price)}%`, "--zone-width": `${Math.max(36, zone.confidence)}%` }}>
            <div /><span>{zone.side === "short" ? "SHORT LIQ" : "LONG LIQ"}</span>
            <strong>{priceText(zone.price)}</strong><small>{zone.leverage}x / C{Math.round(zone.confidence)}</small>
          </div>
        ))}
        <div className="market-room-current" style={{ "--zone-y": `${y(snapshot.price)}%` }}><i /><span>MARK {priceText(snapshot.price)}</span></div>
      </div>
    </section>
  );
}

function Agent({ data, type }) {
  const hermes = type === "hermes";
  const Icon = hermes ? Search : ShieldAlert;
  return (
    <section className={`market-room-panel market-room-agent is-${type}`}>
      <div className="market-room-agent-head">
        <div className="market-room-agent-icon"><Icon className="h-5 w-5" /></div>
        <div><strong>{hermes ? "Hermes" : "OpenClaw"}</strong><span>{hermes ? "Market scout" : "Risk challenger"}</span></div>
        <div className="market-room-agent-confidence">{data ? Math.round(num(data.confidence)) : "--"}<small>CONF</small></div>
      </div>
      {data ? <><div className="market-room-stance">{data.stance || "NO STANCE"}</div><p>{data.summary || data.thesis}</p>
        <div className="market-room-evidence">{(data.evidence || []).slice(0, 4).map((item) => <span key={item}><CheckCircle2 className="h-3 w-3" />{item}</span>)}</div></>
        : <div className="market-room-agent-empty"><Bot className="h-6 w-6" /><span>Waiting for shared snapshot</span></div>}
    </section>
  );
}

function Verdict({ debate }) {
  const verdict = debate?.verdict;
  if (!verdict) return <section className="market-room-panel market-room-verdict is-empty"><Scale className="h-7 w-7" /><div><strong>AEON verdict pending</strong><span>No bounded debate has completed for this snapshot.</span></div></section>;
  return (
    <section className="market-room-panel market-room-verdict">
      <div className="market-room-verdict-main"><div><Scale className="h-5 w-5" /></div><section>
        <span>AEON SYNTHESIS</span><strong>{verdict.action || "WATCH"} / {Math.round(num(debate.confidence))} conviction</strong><p>{verdict.summary}</p>
      </section></div>
      <div className="market-room-verdict-grid">
        <div><span>Trigger</span><strong>{verdict.trigger ? priceText(verdict.trigger) : "Not set"}</strong></div>
        <div><span>Invalidation</span><strong>{verdict.invalidation ? priceText(verdict.invalidation) : "Not set"}</strong></div>
        <div><span>Targets</span><strong>{(verdict.targets || []).map(priceText).join(" / ") || "Not set"}</strong></div>
        <div><span>Expires</span><strong>{debate.expires || "Not set"}</strong></div>
      </div>
    </section>
  );
}

export default function MarketRoomPage({ wsConnected }) {
  const [symbol, setSymbol] = useState("SOL");
  const [timeframe, setTimeframe] = useState("15m");
  const [snapshot, setSnapshot] = useState(null);
  const [debate, setDebate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const loadLive = useCallback(async ({ quiet = false } = {}) => {
    if (inFlight.current) return;   // slow backend: never stack overlapping polls
    inFlight.current = true;
    if (!quiet) setLoading(true);
    setError("");
    const query = new URLSearchParams({ symbol: `${symbol}USDT`, timeframe });
    const [snapshotResult, debateResult] = await Promise.allSettled([
      json(`${API}/market-room/snapshot?${query}`),
      json(`${API}/market-room/debate/latest?${query}`),
    ]);
    if (snapshotResult.status === "fulfilled") {
      setSnapshot(normalizeLive(snapshotResult.value, symbol, timeframe));
    } else if (!quiet) {
      setSnapshot(null);
      setError("Market Room backend is not linked yet.");
    } else {
      // keep the last good snapshot on a failed background refresh
      setError("Live refresh delayed — showing last received snapshot.");
    }
    if (debateResult.status === "fulfilled") setDebate(debateResult.value.debate || debateResult.value);
    else if (!quiet) setDebate(null);
    setLoading(false);
    inFlight.current = false;
  }, [symbol, timeframe]);

  useEffect(() => {
    loadLive();
    const timer = setInterval(() => loadLive({ quiet: true }), 15000);
    return () => clearInterval(timer);
  }, [loadLive]);

  const runDebate = async () => {
    if (!snapshot) return;
    setRunning(true);
    try {
      const result = await json(`${API}/market-room/debate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbol: `${symbol}USDT`, timeframe, snapshot_id: snapshot.snapshotId }),
      });
      setDebate(result.debate || result);
    } catch { setError("The debate endpoint is unavailable."); }
    setRunning(false);
  };

  const activeSnapshot = snapshot;
  const activeDebate = debate;
  const sources = activeSnapshot?.sources || SOURCE_NAMES.map((name) => ({ name, state: "missing" }));
  const risk = num(activeSnapshot?.cascadeRisk);

  return (
    <div className="market-room-page pb-28">
      <header className="market-room-header">
        <div><div className="market-room-eyebrow"><Signal className="h-3.5 w-3.5" />SHARED INTELLIGENCE</div><h2>Market Room</h2><p>{activeSnapshot?.snapshotId || "No shared snapshot"}</p></div>
        <div className="market-room-header-actions">
          <button className="market-room-icon-button" title="Refresh live market room" onClick={() => loadLive()} disabled={loading}><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </header>

      <div className="market-room-controls">
        <div className="market-room-control-group">{SYMBOLS.map((item) => <button key={item} className={symbol === item ? "is-active" : ""} onClick={() => setSymbol(item)}>{item}</button>)}</div>
        <div className="market-room-control-group">{TIMEFRAMES.map((item) => <button key={item} className={timeframe === item ? "is-active" : ""} onClick={() => setTimeframe(item)}>{item}</button>)}</div>
        <button className="market-room-run" disabled={!snapshot || running} onClick={runDebate}><Zap className="h-4 w-4" />{running ? "Debating" : "Run debate"}</button>
      </div>

      {error ? <div className="market-room-notice is-error"><AlertTriangle className="h-4 w-4" />{error}</div> : null}

      <SourceStrip sources={sources} wsConnected={wsConnected} />
      <div className="market-room-metrics">
        <Metric icon={Activity} label="Mark price" value={activeSnapshot ? priceText(activeSnapshot.price) : "--"} detail={activeSnapshot?.symbol || "awaiting snapshot"} />
        <Metric icon={Waves} label="OI impulse" value={activeSnapshot ? `${activeSnapshot.oiImpulse >= 0 ? "+" : ""}${activeSnapshot.oiImpulse.toFixed(2)}x` : "--"} detail={activeSnapshot?.regime || "unclassified"} tone={activeSnapshot?.oiImpulse > 0 ? "positive" : "neutral"} />
        <Metric icon={Signal} label="Flow delta" value={activeSnapshot ? `${activeSnapshot.flowDelta >= 0 ? "+" : ""}${activeSnapshot.flowDelta.toFixed(1)}%` : "--"} detail="taker buy / sell" tone={activeSnapshot?.flowDelta > 0 ? "positive" : "negative"} />
        <Metric icon={Gauge} label="Funding" value={activeSnapshot ? `${activeSnapshot.funding.toFixed(3)}%` : "--"} detail="perpetual rate" tone={activeSnapshot?.funding > 0.01 ? "warning" : "neutral"} />
        <Metric icon={AlertTriangle} label="Cascade" value={activeSnapshot ? Math.round(risk) : "--"} detail={risk >= 65 ? "high" : risk >= 45 ? "elevated" : "low"} tone={risk >= 65 ? "negative" : risk >= 45 ? "warning" : "positive"} />
      </div>

      <PerpFlowPanel />
      <WeexMarketPanel />

      <div className="market-room-layout">
        <Ladder snapshot={activeSnapshot} />
        <section className="market-room-panel market-room-integrity">
          <div className="market-room-panel-head"><div><span>Data integrity</span><strong>Snapshot contract</strong></div><Database className="h-4 w-4" /></div>
          <dl><div><dt>Snapshot</dt><dd>{activeSnapshot?.snapshotId || "Not linked"}</dd></div><div><dt>Quality</dt><dd>{activeSnapshot ? `${Math.round(activeSnapshot.quality)}%` : "--"}</dd></div><div><dt>Timeframe</dt><dd>{activeSnapshot?.timeframe || timeframe}</dd></div><div><dt>Mode</dt><dd>LIVE</dd></div></dl>
          <div className="market-room-pipeline"><span><Database className="h-3.5 w-3.5" />Data</span><i /><span><Search className="h-3.5 w-3.5" />Hermes</span><i /><span><ShieldAlert className="h-3.5 w-3.5" />OpenClaw</span><i /><span><Scale className="h-3.5 w-3.5" />AEON</span></div>
        </section>
      </div>

      <div className="market-room-agents"><Agent data={activeDebate?.hermes} type="hermes" /><Agent data={activeDebate?.openclaw} type="openclaw" /></div>
      <Verdict debate={activeDebate} />
      <div className="market-room-footer-status"><span><Clock3 className="h-3.5 w-3.5" />{activeDebate?.expires || "No expiry"}</span><span><Target className="h-3.5 w-3.5" />{activeSnapshot?.zones?.length || 0} ranked zones</span><span><Activity className="h-3.5 w-3.5" />{wsConnected ? "Event stream linked" : "Event stream offline"}</span></div>
    </div>
  );
}
